import { ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { usePostAutomatonCommandMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';

import { AutomatonCommandEnum, AutomatonDefenceCommand, AutomatonSleepAllCabinetsCommand } from '@/types/c2/c2.types';
import { ActionEnum } from '@/types/commons/commons.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';
import { AutomatonSensorConfiguration, AutomatonSensorStatus } from '@/types/sensor/sensors';

export interface UseAutomatonCommandParameters {
  automatonStatus: AutomatonSensorStatus | null;
  automatonConfiguration: AutomatonSensorConfiguration | null;
}

export interface UseAutomatonCommandFunctions {
  canCommand: boolean;
  canRepulse: boolean;
  canDetect: boolean;
  repulse: (enableRepulsion: boolean) => void;
  sleepAll: (powerOnCabinets: boolean) => void;
  isSleepAllCommandLoading: boolean;
  isRepulsionCommandLoading: boolean;
}

export const useAutomatonCommand: (params: UseAutomatonCommandParameters) => UseAutomatonCommandFunctions = ({
  automatonStatus,
  automatonConfiguration,
}: UseAutomatonCommandParameters) => {
  const [postSleepAllCommand, { isLoading: isSleepAllLoading }] = usePostAutomatonCommandMutation();
  const [postRepulsionCommand, { isLoading: isRepulsionLoading }] = usePostAutomatonCommandMutation();

  const canSendPerimeterCommands = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.COMMAND, sensorFamily: SensorFamilyEnum.AUTOMATON }),
  );
  const canSendRepulsionCommand = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.ACTIVATE_REPULSION,
      sensorFamily: SensorFamilyEnum.AUTOMATON,
    }),
  );
  const canSendDetectionCommand = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, {
      action: ActionEnum.DEACTIVATE_REPULSION,
      sensorFamily: SensorFamilyEnum.AUTOMATON,
    }),
  );

  const handleCabinetsPower = (powerOnCabinets: boolean) => {
    if (automatonStatus !== null && automatonConfiguration !== null) {
      const command: AutomatonSleepAllCabinetsCommand = {
        type: AutomatonCommandEnum.AUTOMATON_COMMAND_SLEEP_ALL_CABINETS,
        sensorCode: automatonConfiguration.code,
        sleep: !powerOnCabinets,
      };
      const toastParams = {
        commandValues: { state: powerOnCabinets },
        command: 'sensors.commands.automaton.sleepCommand',
        name: automatonConfiguration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postSleepAllCommand({
        site: automatonConfiguration.site,
        appCode: automatonConfiguration.appCode,
        command,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  };

  const handleAutomatonRepulsion = (enableRepulsion: boolean) => {
    if (automatonStatus !== null && automatonConfiguration !== null) {
      const command = {
        type: AutomatonCommandEnum.AUTOMATON_COMMAND_DEFENCE,
        sensorCode: automatonConfiguration.code,
        defence: enableRepulsion,
      } as AutomatonDefenceCommand;
      const toastParams = {
        commandValues: { state: enableRepulsion },
        command: 'sensors.commands.automaton.defenceCommand',
        name: automatonConfiguration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postRepulsionCommand({
        site: automatonConfiguration.site,
        appCode: automatonConfiguration.appCode,
        command,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  };

  return {
    canCommand: canSendPerimeterCommands,
    canDetect: canSendDetectionCommand,
    canRepulse: canSendRepulsionCommand,
    isRepulsionCommandLoading: isRepulsionLoading,
    isSleepAllCommandLoading: isSleepAllLoading,
    repulse: handleAutomatonRepulsion,
    sleepAll: handleCabinetsPower,
  };
};
